import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import moment from 'moment';
import { ago, daysAgo } from 'src/app/utils/date';

@Component({
    selector: 'pays-past-due',
    templateUrl: './pays-past-due.component.html',
    styleUrls: ['./pays-past-due.component.scss'],
    standalone: false
})
export class PaysPastDueComponent implements OnInit {

    // Unique internal identity of the lot whose past due payments will be queried for.
    @Input() lotId: number = 0

    // Unique internal identity of the contact whose past due payments will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Unique internal identity of the sale finance type to filter the queried past due payments with.
    @Input() financeTypeId: number = 0

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    /** Notify other components that a past due account was chosen by the user. */
    @Output() accountChosen: EventEmitter<Object> = new EventEmitter()

    //
    protected payments: Array<any> = []

    //
    protected pastDues: Array<any> = []

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // The currently selected tersm (receivable) identity
    protected termsId: number = 0

    constructor (private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService) {}

    ngOnInit() {
        this.fetch()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId || changes.contactId || changes.financeTypeId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
            lotParam = ''
        }

        let financeTypeParam = ''
        if (this.financeTypeId && (this.financeTypeId > 0)) {
            financeTypeParam = `&financeType=${this.financeTypeId}`
        }

        this.loading = true
        if (this.page === 1) {
            this.payments = []
            this.pastDues = []
            this.termsId = 0
        }

        this.master.get(`worklist?page=${this.page}&sort=due|ASC${lotParam}${contactParam}${financeTypeParam}&span=past`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.payments = this.payments.concat(res.data.receivables)
                this.constructPastDues(res.data.receivables)
                return
            }
  
            this.payments = res.data.receivables
            if (!this.payments || (this.payments.length <= 0)) {
                return
            }

            this.constructPastDues(res.data.receivables)

            // choose the first available workable account
            let payment = this.payments.find((pay) => pay.workable)
            if (!payment) {
                payment = this.payments[0]
            }

            const meta = {
                termsId: parseInt(payment['termsId']),
                saleId: parseInt(payment['saleId']),
                contactId: parseInt(payment['contactId'])
            }

            this.termsId = meta.termsId
            this.accountChosen.emit(meta)        
        })
    }

    /**
     * Using the payments data we received lets build a list 
     * of past due accounts we can use to quickly populate 
     * the UI listing.
     * @param {Array} receivables A list of receivables to append to the past due list.
     */
    private constructPastDues = (receivables) => {
        if (!receivables || (receivables.length <= 0)) {
            return
        }

        for (const receivable of receivables) {
            const pastDue = {
                termsId: parseInt(receivable.termsId || 0),
                saleId: parseInt(receivable.saleId || 0),
                contactId: parseInt(receivable.contactId || 0),
                contact: receivable.contact,
                stock: receivable.stock,
                vehicle: receivable.vehicle,
                url: receivable.url,
                vin: receivable.vin,
                category: receivable.category,
                due: receivable.pastDueDate || receivable.due,
                amount: 0,
                worked: receivable.worked,
                workable: receivable.workable,
                insurance: this.insuranceMessage(receivable),
                insuranceClass: this.insuranceClass(receivable),
                categories: [],
                items: []
            }

            if (receivable.deposit && receivable.depositDue && (ago(receivable.depositDue) === 'past')) {
                pastDue.items.push({
                    category: 'deposit',
                    amount: receivable.deposit,
                    due: receivable.depositDue
                })

                pastDue.amount += receivable.deposit
                pastDue.categories.push('deposit')
            }

            if (receivable.initial && receivable.initialDue && (ago(receivable.initialDue) === 'past')) {
                pastDue.items.push({
                    category: 'initial down',
                    amount: receivable.initial,
                    due: receivable.initialDue
                })

                pastDue.amount += receivable.initial
                pastDue.categories.push('initial down')
            }

            if (receivable.deferred && receivable.deferredDue && (ago(receivable.deferredDue) === 'past')) {
                pastDue.items.push({
                    category: 'deferred down',
                    amount: receivable.deferred,
                    due: receivable.deferredDue
                })

                pastDue.amount += receivable.deferred
                pastDue.categories.push('deferred down')
            }

            if (receivable.loan && receivable.loanDue && (ago(receivable.loanDue) === 'past')) {
                pastDue.items.push({
                    category: 'mv loan',
                    amount: receivable.loan,
                    due: receivable.loanDue
                })

                pastDue.amount += receivable.loan
                pastDue.categories.push('mv loan')
            }

            this.pastDues.push(pastDue)
        }
    }

    protected insuranceMessage = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'no insurance'
        }

        if (receivable.insuranceExpires && receivable.insuranceIsCpi) {
            return this.cpiMessage(receivable.insuranceExpires)
        }

        return this.insMessage(receivable.insuranceProvider, receivable.insuranceExpires)
    }

    protected insuranceClass = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'danger'
        }

        return this.insClass(receivable.insuranceExpires)
    }

    private cpiMessage = (ends: string): string => {
        if (!ends) {
            return 'no cpi'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `cpi expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `cpi expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return 'cpi in good standing'
    }

    private insClass = (ends: string): string => {
        if (!ends) {
            return 'secondary'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 30) {
            return 'danger'
        }

        if (days <= 60) {
            return 'warning'
        }

        return 'success'
    }

    private insMessage = (provider: string, ends: string): string => {
        if (!ends) {
            return 'no insurance'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `ins. expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `ins. expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return `outside ins. active`
    }

    protected icon = (category: string) => {
        if (!category || (category.length <= 0)) {
            return 'monetization_on'
        }

        switch(category.toLowerCase()) {
            case 'mv loan':
                return 'directions_car'
            case 'service':
                return 'car_repair'
            case 'deposit':
            case 'initial down':
            case 'deferred down':
                return 'payments'
            default:
                return 'monetization_on'
        }
    }

    protected dueToClass = (due: string): string => {
        return ago(due)
    }

    protected daysPastDue = (due: string): number => {
        return daysAgo(due)
    }

    /**
     * A payment in the list was clicked. 
     * @param payment The payment object that was chosen by the user.
     */
    protected selected = (e: Event, payment: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!payment || !payment['termsId']) {
            return
        }

        const meta = {
            termsId: parseInt(payment['termsId']),
            saleId: parseInt(payment['saleId']),
            contactId: parseInt(payment['contactId'])
        }

        this.termsId = meta.termsId
        this.accountChosen.emit(meta)
    }
}
